interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  age: number
  ttl: number
  size: number
  color: string
  shape: 'spark' | 'confetti' | 'dust'
  rot: number
  vr: number
  gravity: number
}

export interface BurstOptions {
  count?: number
  colors?: string[]
  speed?: number
  shape?: Particle['shape']
  spread?: number
}

/**
 * Minimal particle emitter on a transparent canvas that sits over the scene.
 * Coordinates are normalised (0..1) so the scene can be any size.
 */
export class ParticleSystem {
  private ctx: CanvasRenderingContext2D
  private particles: Particle[] = []
  private raf = 0
  private last = 0
  private width = 0
  private height = 0

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!
    this.resize()
  }

  resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const rect = this.canvas.getBoundingClientRect()
    this.width = rect.width
    this.height = rect.height
    this.canvas.width = Math.round(rect.width * dpr)
    this.canvas.height = Math.round(rect.height * dpr)
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  burst(nx: number, ny: number, opts: BurstOptions = {}) {
    const {
      count = 28,
      colors = ['#ffd166', '#ff7a45', '#ffffff', '#ff3b30'],
      speed = 1,
      shape = 'spark',
      spread = Math.PI * 2,
    } = opts
    const base = Math.min(this.width, this.height)
    const x = nx * this.width
    const y = ny * this.height
    for (let i = 0; i < count; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * spread
      const v = (0.25 + Math.random() * 0.55) * base * speed
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * v,
        vy: Math.sin(angle) * v,
        age: 0,
        ttl: 1.45 + Math.random() * 0.45,
        size: shape === 'dust' ? base * 0.02 : base * (0.004 + Math.random() * 0.006),
        color: colors[Math.floor(Math.random() * colors.length)],
        shape,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 12,
        gravity: shape === 'dust' ? base * 0.3 : base * 1.6,
      })
    }
    this.start()
  }

  confetti() {
    const colors = ['#ffd166', '#06d6a0', '#118ab2', '#ef476f', '#ffffff', '#8338ec']
    const base = Math.min(this.width, this.height)
    for (let i = 0; i < 180; i++) {
      const x = Math.random() * this.width
      this.particles.push({
        x,
        y: -10 - Math.random() * this.height * 0.3,
        vx: (Math.random() - 0.5) * base * 0.3,
        vy: base * (0.2 + Math.random() * 0.3),
        age: 0,
        ttl: 2.2 + Math.random() * 1.5,
        size: base * (0.008 + Math.random() * 0.008),
        color: colors[Math.floor(Math.random() * colors.length)],
        shape: 'confetti',
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 8,
        gravity: base * 0.35,
      })
    }
    this.start()
  }

  clear() {
    this.particles = []
    this.ctx.clearRect(0, 0, this.width, this.height)
  }

  private start() {
    if (this.raf) return
    this.last = performance.now()
    this.raf = requestAnimationFrame(this.frame)
  }

  private frame = (t: number) => {
    const dt = Math.min((t - this.last) / 1000, 0.05)
    this.last = t
    const ctx = this.ctx
    ctx.clearRect(0, 0, this.width, this.height)
    const alive: Particle[] = []
    for (const p of this.particles) {
      p.age += dt
      if (p.age >= p.ttl) continue
      p.vy += p.gravity * dt
      if (p.shape === 'confetti') p.vx *= 0.99
      p.x += p.vx * dt
      p.y += p.vy * dt
      p.rot += p.vr * dt
      const life = 1 - p.age / p.ttl
      ctx.save()
      ctx.globalAlpha = p.shape === 'confetti' ? Math.min(1, life * 3) : life
      ctx.fillStyle = p.color
      ctx.translate(p.x, p.y)
      ctx.rotate(p.rot)
      if (p.shape === 'spark') {
        ctx.fillRect(-p.size * 2, -p.size / 2, p.size * 4, p.size)
      } else if (p.shape === 'confetti') {
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2)
      } else {
        ctx.beginPath()
        ctx.arc(0, 0, p.size * (1 + p.age), 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.restore()
      alive.push(p)
    }
    this.particles = alive
    this.raf = alive.length ? requestAnimationFrame(this.frame) : 0
    if (!alive.length) ctx.clearRect(0, 0, this.width, this.height)
  }
}
